import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Download, ReceiptText, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "../lib/api";
import { fmtMoney, DEFAULT_CURRENCY } from "../lib/money";
import EmptyState from "../components/common/EmptyState";

const WS_KEY = "lg_current_workspace_id";

function isoMonthToday() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function shiftMonth(ym, delta) {
  const [y, m] = ym.split("-").map((n) => parseInt(n, 10));
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function formatMonthLabel(ym, locale) {
  const [y, m] = ym.split("-").map((n) => parseInt(n, 10));
  const d = new Date(y, m - 1, 1);
  return new Intl.DateTimeFormat(locale.startsWith("fr") ? "fr-CA" : "en-CA", {
    month: "long", year: "numeric",
  }).format(d);
}

export default function InvoicesSummary() {
  const { t, i18n } = useTranslation("invoices");
  const [searchParams, setSearchParams] = useSearchParams();
  const initialMonth = searchParams.get("month") || isoMonthToday();
  const [month, setMonth] = useState(initialMonth);
  const [vendorFilter, setVendorFilter] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSummary = useCallback(async (m, v) => {
    setLoading(true);
    setError(null);
    try {
      const params = { month: m };
      if (v && v.trim()) params.vendor_name = v.trim();
      const r = await api.get("/invoices/summary", { params });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Unknown error");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSummary(month, vendorFilter);
    setSearchParams(vendorFilter ? { month, vendor_name: vendorFilter } : { month });
  }, [month]);

  const handleVendorBlur = () => {
    fetchSummary(month, vendorFilter);
    setSearchParams(vendorFilter ? { month, vendor_name: vendorFilter } : { month });
  };

  const exportCsv = async () => {
    try {
      const wsid = localStorage.getItem(WS_KEY) || "";
      const url = new URL(`${API}/invoices/summary/export`);
      url.searchParams.set("month", month);
      if (vendorFilter && vendorFilter.trim()) {
        url.searchParams.set("vendor_name", vendorFilter.trim());
      }
      const r = await fetch(url.toString(), {
        credentials: "include",
        headers: { "X-Workspace-Id": wsid },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const dlUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      a.download = `invoices_summary_${month}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(dlUrl);
      toast.success(t("summary.csv_started"));
    } catch (e) {
      toast.error(`${t("summary.csv_failed")} — ${e.message}`);
    }
  };

  const totals = data?.totals;
  const byVendor = data?.by_vendor || [];
  const hasData = (totals?.invoice_count || 0) > 0;
  const monthLabel = useMemo(() => formatMonthLabel(month, i18n.language || "fr"),
                             [month, i18n.language]);

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6" data-testid="invoices-summary-page">
      {/* Header */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-qenva-cat-invoice font-bold mb-1">
            <ReceiptText size={11} className="inline mr-1" />
            {t("summary.page_title")}
          </p>
          <h1 className="font-display font-bold text-qenva-navy text-3xl tracking-tight">
            {monthLabel}
          </h1>
          <p className="text-sm text-zinc-500 mt-1 max-w-xl">{t("summary.page_subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Month nav */}
          <div className="inline-flex items-center gap-1 rounded-lg border border-zinc-200 bg-white px-1 py-0.5">
            <button
              type="button"
              onClick={() => setMonth(shiftMonth(month, -1))}
              data-testid="invoices-prev-month"
              className="p-1.5 hover:bg-zinc-50 rounded text-zinc-600"
              title={t("summary.prev_month")}
              aria-label={t("summary.prev_month")}
            >
              <ChevronLeft size={15} />
            </button>
            <span className="px-2 text-[12.5px] font-semibold tabular-nums text-qenva-navy">{month}</span>
            <button
              type="button"
              onClick={() => setMonth(shiftMonth(month, +1))}
              data-testid="invoices-next-month"
              className="p-1.5 hover:bg-zinc-50 rounded text-zinc-600"
              title={t("summary.next_month")}
              aria-label={t("summary.next_month")}
            >
              <ChevronRight size={15} />
            </button>
          </div>
          {/* Vendor filter */}
          <input
            type="text"
            value={vendorFilter}
            onChange={(e) => setVendorFilter(e.target.value)}
            onBlur={handleVendorBlur}
            onKeyDown={(e) => { if (e.key === "Enter") handleVendorBlur(); }}
            placeholder={t("summary.vendor_filter_placeholder")}
            data-testid="invoices-vendor-filter"
            className="px-3 py-1.5 text-[13px] rounded-lg border border-zinc-200 focus:ring-2 focus:ring-qenva-blue/30 focus:border-qenva-blue outline-none"
          />
          {/* CSV export */}
          <button
            type="button"
            onClick={exportCsv}
            disabled={!hasData}
            data-testid="invoices-export-csv"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[13px] font-bold bg-qenva-navy text-white hover:bg-[#13325A] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Download size={13} />
            {t("summary.export_csv")}
          </button>
        </div>
      </header>

      {/* KPIs */}
      <section
        className="grid grid-cols-2 sm:grid-cols-4 gap-3"
        data-testid="invoices-kpis"
      >
        {[
          { key: "total_invoiced", value: totals?.grand_total_invoiced, money: true, accent: "navy" },
          { key: "total_unpaid",   value: totals?.grand_total_unpaid,   money: true, accent: "danger" },
          { key: "invoice_count",  value: totals?.invoice_count,        money: false, accent: "navy" },
          { key: "vendors_count",  value: totals?.vendors_count,        money: false, accent: "navy" },
        ].map((kpi) => (
          <div
            key={kpi.key}
            data-testid={`invoices-kpi-${kpi.key}`}
            className={`rounded-xl border bg-white p-4 ${
              kpi.accent === "danger" && kpi.value > 0
                ? "border-qenva-cat-contract/30 bg-qenva-cat-contract/5"
                : "border-zinc-200"
            }`}
          >
            <p className="text-[10.5px] uppercase tracking-wider font-bold text-zinc-500">
              {t(`summary.totals.${kpi.key}`)}
            </p>
            <p className={`mt-1 text-[24px] font-display font-bold tabular-nums tracking-tight ${
              kpi.accent === "danger" && kpi.value > 0
                ? "text-qenva-cat-contract"
                : "text-qenva-navy"
            }`}>
              {kpi.value === undefined || kpi.value === null
                ? "—"
                : kpi.money
                  ? fmtMoney(kpi.value, totals?.currency || DEFAULT_CURRENCY, { compact: false, minimumFractionDigits: 2 })
                  : kpi.value}
            </p>
          </div>
        ))}
      </section>

      {/* Vendor table */}
      <section
        className="rounded-xl border border-zinc-200 bg-white"
        data-testid="invoices-vendor-table"
      >
        {loading ? (
          <div className="p-10 text-center text-zinc-400">
            <Loader2 size={20} className="inline animate-spin mr-2" />
            {t("summary.loading")}
          </div>
        ) : error ? (
          <div className="p-10 text-center text-qenva-cat-contract" data-testid="invoices-error">
            {t("summary.error")}: {error}
          </div>
        ) : !hasData ? (
          <EmptyState
            icon={ReceiptText}
            title={t("summary.empty_title")}
            subtitle={t("summary.empty_subtitle")}
            testId="invoices-empty"
            variant="subtle"
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-zinc-200 bg-zinc-50/50">
                  {["vendor", "invoice_count", "total_invoiced", "total_paid", "total_unpaid", "average", "last_date"].map((col, idx) => (
                    <th
                      key={col}
                      scope="col"
                      className={`text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 font-bold px-4 py-3 ${
                        idx === 0 ? "text-left" : "text-right"
                      }`}
                    >
                      {t(`summary.table.col_${col}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {byVendor.map((v, i) => (
                  <tr
                    key={`${v.vendor_name}-${i}`}
                    data-testid={`invoices-row-${i}`}
                    className="border-b border-zinc-100 hover:bg-zinc-50/50 transition-colors"
                  >
                    <td className="px-4 py-3 font-semibold text-qenva-navy">{v.vendor_name}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-zinc-600">{v.invoice_count}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-bold text-qenva-navy">
                      {fmtMoney(v.total_invoiced, totals?.currency, { compact: false, minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-qenva-cat-financing">
                      {fmtMoney(v.total_paid, totals?.currency, { compact: false, minimumFractionDigits: 2 })}
                    </td>
                    <td className={`px-4 py-3 text-right tabular-nums ${
                      v.total_unpaid > 0 ? "text-qenva-cat-contract font-bold" : "text-zinc-400"
                    }`}>
                      {fmtMoney(v.total_unpaid, totals?.currency, { compact: false, minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-zinc-600">
                      {fmtMoney(v.average_amount, totals?.currency, { compact: false, minimumFractionDigits: 2 })}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums text-zinc-500">
                      {v.last_invoice_date || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
